import type { Money } from "./application";
import { PetPolicyTemplateSettings } from "./pet-policy";

interface VerificationStep {
  applicant_id: number;
  application_id: number;
  index: number;
  name: string;
  verification_id: number;
  json_schema: object;
  data: Record<string, unknown>;
}

export enum VerificationStatus {
  Pending = "pending",
  Verified = "verified",
  Finished = "finished",
  Failed = "failed",
  FinishedWithWarnings = "finished_with_warnings",
  PassedWithWarnings = "passed_with_warnings",
  FinishedByOverridden = "finished_by_overridden",
  FinishedByBypassed = "finished_by_bypassed",
  Started = "started",
  Canceled = "canceled",
  Submitted = "submitted",
  Processing = "processing",
  ManuallyApproved = "manually_approved",
  NoDataAvailable = "no_data_available",
  Skipped = "skipped",
  EmailSent = "email_sent",
  Completed = "completed"
}

export enum ExecutionModes {
  Automatic = "automatic",
  Manual = "manual",
  Conditionally = "conditionally"
}

/**
 * When a partner's animal screening invite is configured to go out. On approval
 * means nothing is sent — and nothing is expected back — until the application is
 * approved, so a declined application never screens at all.
 */
export enum PetScreeningTrigger {
  OnSubmission = "on_submission",
  OnApproval = "on_approval"
}

export interface VerificationOverriddenBy {
  id?: number;
  name: string;
  at: string;
}

export interface VerificationBypassRequest {
  reason: string | null;
  requested_at: string;
}

export enum VerificationBypassOutcome {
  Approved = "approved",
  Declined = "declined",
  Withdrawn = "withdrawn"
}

export interface VerificationBypassResolution {
  outcome: VerificationBypassOutcome;
  resolved_by: VerificationOverriddenBy | null;
  resolved_at: string;
  decline_note: string | null;
}

export enum RerunRequirement {
  DecisionHasBeenMade = "decision_has_been_made",
  Pass60DaysPeriod = "pass_60_days_period",
  ApplicationOnHold = "application_on_hold",
  ApplicationCanceled = "application_canceled"
}

export enum CanadianCriminalReportState {
  InProgress = "in_progress",
  Issue = "issue",
  Expired = "expired",
  Done = "done",
  NotResident = "not_resident"
}

export interface CanadianCriminalReportSummary {
  report_state?: CanadianCriminalReportState | null;
  invited_at?: string | null;
  report_date?: string | null;
}

export interface VerificationRequestedBy {
  id: number;
  name: string;
  email: string;
}

// combined income stuff
export enum IncomeType {
  Employment = "employment",
  SelfEmployment = "self_employment",
  OtherIncome = "other",
  OfferLetter = "offer_letter",
  NoIncome = "no_income",
  // Housing assistance is pseudo income type
  HousingAssistance = "housing_assistance"
}

export enum IncomeStatus {
  Pending = "pending", // When income created --> initial status
  Processing = "processing", // when document is upload that needs to be processed. We scheduled sidekiq job || before document was uploaded to Ocrolus
  Submitted = "submitted", // when offer letter or HA --> success for these
  Failed = "failed", // failed for plaid/ocrolus/inscribe
  Deleted = "deleted", // never should come. When in AX customer deletes income. PX should never have it. Keeping just in case
  Verified = "verified", // success for plaid/ocrolus/inscribe
  VerifiedWithWarnings = "verified_with_warnings", // success for plaid/ocrolus/inscribe
  FinishedByOverride = "finished_by_override" // verification manually marked as complete in PX
}

export interface IncomeItem {
  amount?: Money;
  id: number;
  pay_period?: string; // Yevhenii said that we should do ENUM
  start_date?: string;
  status: IncomeStatus;
  type: IncomeType;
  additional_data?: {
    supervisor?: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
    };
    description?: string;
    paid_from?: string;
    company_name?: string;
    job_title?: string;
  };
}

export enum VerifyMethodType {
  Bank = "bank",
  Payroll = "payroll",
  Paystub = "paystub",
  BankStatement = "bank_statement",
  W2 = "w2",
  // Both 1099 kinds; the API detects which.
  Form1099 = "form_1099",
  OfferLetter = "offer_letter",
  // HousingAssistance is not a verify method, it is a pseudo income type
  HousingAssistance = "housing_assistance",
  BenefitsStatement = "benefits_statement",
  Documents = "documents" // Not a real verify method - is added on FE for FE-only usage. Stands as a title for group of document-oriented verifications: paystub, bank_statment, w2
}

export enum IncomeSourceStatus {
  Pending = "pending",
  Started = "started",
  Processing = "processing",
  Failed = "failed",
  Finished = "finished",
  Deleted = "deleted",
  FinishedWithWarnings = "finished_with_warnings",
  Submitted = "submitted",
  SubmittedWithWarnings = "submitted_with_warnings",
  Reset = "reset",
  Duplicate = "duplicate"
}

export enum PayPeriod {
  Monthly = "monthly",
  Annually = "annually",
  BiWeekly = "bi_weekly",
  Weekly = "weekly"
}

export enum IncomeSourceProvider {
  Inscribe = "inscribe",
  Plaid = "plaid",
  Ocrolus = "ocrolus",
  Argyle = "argyle"
}

type InscribeProcessingStatus = null | "CLASSIFIED" | "PARSED" | "DONE";

export interface IncomeSource {
  id: number;
  status: IncomeSourceStatus;
  type: Exclude<VerifyMethodType, VerifyMethodType.Documents>;
  provider?: IncomeSourceProvider;
  income_id: number | null;
  trust_score?: number;
  has_available_items: boolean;
  items: {
    id: number;
    income_source_id: number;
    income_id: number | null;
    selected_by_applicant?: boolean;
    data: {
      status?: "ACTIVE" | "INACTIVE";
      employer?: {
        name?: string | null;
      };
      employment: {
        status?: "ACTIVE" | "INACTIVE";
        employer?: {
          name?: string | null;
        };
      };
      institution_metadata?: {
        name?: string | null;
      } | null;
      // bank items only (payroll items expose no masked account number)
      account?: {
        name?: string | null;
        mask?: string | null;
      } | null;
      income_description?: string | null;
      end_date: string;
      start_date: string;
      net_income_summary?: object[];
      net_monthly_income: Money;
      net_monthly_income_amount: number;
      pay_frequency?: string | null;
    };
  }[];
  inscribe_status?: InscribeProcessingStatus;
  assets?: { asset_url: string | null; asset_name: string | null }[];
}

export enum VerificationStartRequirements {
  Phone = "phone",
  IdentityVerification = "identity_verification",
  OtherVerifications = "other_verifications"
}

export interface Verification {
  id: number;
  name: VerificationNames;
  status: VerificationStatus;
  editable: boolean;
  index: number;
  estimated_time: number;
  title: string;
  description: string;
  progress?: {
    total_steps: number;
    filled_steps: number;
  };
  requested_at?: string;
  created_at: string;
  started_at?: string | null;
  completed_at?: string;
  updated_at: string;
  steps?: VerificationStep[];
  vendor?: DataVendors;
  settings?: {
    execution_mode?: ExecutionModes;
    provider?: string;
    selfie_check?: boolean;
    identity_abuse_check?: {
      stolen_identity_threshold?: number;
      synthetic_identity_threshold?: number;
    };
    prevent_apply_if_failed?: boolean;
    policy?: PetPolicyTemplateSettings;
    trigger_on?: PetScreeningTrigger;
  };
  schema?: {
    version: number;
  };
  summary?: {
    ssn_mismatched?: boolean;
  } & CanadianCriminalReportSummary;
  overridden_by: VerificationOverriddenBy | null;
  bypassed_by?: VerificationOverriddenBy | null;
  bypass_request?: VerificationBypassRequest | null;
  bypass_resolution?: VerificationBypassResolution | null;
  skip_reason?: string | null;
  fee: Money;
  combined_income_re_verification_fee?: Money;
  rerun_requirements: RerunRequirement[];
  is_rerun: boolean;
  is_post_created?: boolean;
  requested_by: VerificationRequestedBy | null;
  landlord_has_sent_surveys: Record<
    string,
    { property_address_index: number; status: SurveyStatus }[]
  >;
  landlord_surveys_data?: Record<
    string,
    { index: number; data?: Record<string, string> }[]
  >;
  // combined income stuff
  income_sources?: IncomeSource[];
  incomes?: IncomeItem[];
  has_active_submission_disclosure_link?: boolean;
  bypass_document_verification_enabled?: boolean;
  combined_income_purge_enabled?: boolean;
  income_validation?: IncomeValidation;
  payroll_income_provider?:
    | IncomeSourceProvider.Argyle
    | IncomeSourceProvider.Plaid;
  start_requirements?: VerificationStartRequirements[];
  archived_at?: string;
  rerun_version?: number | null;
  // boom_finance_verification only (PLT-728). The applicant chose
  // "I do not need financing"; the verification is finished without a report.
  declined_financing?: boolean;
  declined_financing_at?: string;
}

export enum IncomeValidationMode {
  Realtime = "realtime",
  Deferred = "deferred"
}

type IncomeValidationModeKey =
  | VerifyMethodType.Paystub
  | VerifyMethodType.W2
  | VerifyMethodType.Form1099;

interface IncomeValidationModeEntry {
  mode: IncomeValidationMode;
  vendor: DocumentVerificationProvider;
  resolved_at: string;
}

export type IncomeValidation = Partial<
  Record<IncomeValidationModeKey, IncomeValidationModeEntry>
>;

export enum DataVendors {
  Cleara = "cleara",
  CIC = "cic",
  Certn = "certn",
  Plaid = "plaid",
  Persona = "persona",
  TransUnion = "transunion",
  Equifax = "equifax",
  Argyle = "argyle"
}

export enum CreditReportModels {
  VantageScore = "vantage_score_model",
  FicoScore = "fico_score_model",
  ResidentScore = "resident_score_model"
}

export enum AnimalScreeningReportVendors {
  PetScreening = "petscreening_service",
  OurPetPolicy = "ourpetpolicy"
}

export const CreditReportModelsNumbers = {
  [CreditReportModels.VantageScore]: "05402",
  [CreditReportModels.FicoScore]: "05206",
  [CreditReportModels.ResidentScore]: "00A72"
};

export const CreditReportModelsNumbersToNames = {
  "05402": "Vantage Score 4",
  "05206": "FICO 9",
  //transUnion
  "00W18": "FICO 9",
  "001NN": "Vantage Score 4",
  "00A72": "Resident Score",
  //transUnion Canada
  "00093": "CreditVision"
};

export enum VerificationNames {
  CreditReport = "credit_report",
  EvictionReport = "eviction_report",
  HousingHistory = "housing_history",
  RentalVerification = "rental_verification",
  PremiumRentalHistory = "premium_rental_history",
  RentalApplication = "rental_application",
  IncomeVerification = "income_verification", // Legacy. Should never come since CombinedIncome release
  VerifiedIncomeVerification = "verified_income_verification", // Legacy. Should never come since CombinedIncome release
  IdentityVerification = "identity_verification",
  CriminalReport = "criminal_report",
  EnhancedCriminalReport = "enhanced_criminal_report",
  CanadianCriminalReport = "canadian_criminal_report",
  CanadianCreditReport = "canadian_credit_report",
  TermsAndConditions = "terms_and_conditions",
  SubmissionDisclosure = "submission_disclosure",
  PetPolicy = "pet_policy",
  PetScreeningReport = "pet_screening_report",
  CombinedIncome = "combined_income_verification",
  EmploymentVerification = "employment_verification",
  TinVerification = "identity_verification_tin",
  WarningsReport = "warnings_report",
  PremiumWarningsReport = "premium_warnings_report",
  BoomFinanceVerification = "boom_finance_verification"
}

// Verifications which exist as branch of verification:
// i.e. CreditReport fully controls CountyWalkIn
export enum PseudoVerificationNames {
  CountyWalkIn = "county_walk_in"
}

export const DISCLOSURES: [VerificationNames, VerificationNames] = [
  VerificationNames.TermsAndConditions,
  VerificationNames.SubmissionDisclosure
];

export enum TimeUnit {
  Months = "months",
  Years = "years"
}

export interface TimeFrame {
  number: number;
  unit: TimeUnit;
}

export const enum SurveyStatus {
  OriginalSent = "original_sent",
  FirstNudgeSent = "first_nudge_sent",
  SecondNudgeSent = "second_nudge_sent",
  FinalNudgeSent = "final_nudge_sent",
  NoResponse = "no_response",
  Failed = "failed",
  Delivered = "delivered",
  // Lite (Typeform) surveys store "finished"; Premium surveys store "completed".
  Finished = "finished",
  Completed = "completed",
  Declined = "declined",
  Pending = "pending",
  Started = "started",
  Canceled = "canceled"
}

export enum FieldDisplaySetting {
  Hidden = "hidden",
  Optional = "optional",
  Required = "required"
}

export enum ReverifyRequestStatus {
  Pending = "pending",
  Processing = "processing",
  Cancelled = "cancelled",
  Completed = "completed",
  Failed = "failed"
}

export enum ReverifyRequestType {
  ApplicantRequest = "applicant_request",
  DocumentsReVerify = "documents_reverify"
}

export enum ReverifyRequestFailureReason {
  NoAvailableIncome = "no_available_income"
}

export interface ReverifyRequestActor {
  id: number | null;
  type: string | null;
  name?: string | null;
}

export interface ReverifyRequest {
  id: string;
  status: ReverifyRequestStatus;
  request_type: ReverifyRequestType;
  income_id: number | null;
  income_type?: string | null;
  methods: VerifyMethodType[];
  failure_reason?: ReverifyRequestFailureReason | null;
  created_at: string;
  updated_at: string;
  created_by?: ReverifyRequestActor;
  cancelled_by?: ReverifyRequestActor;
  completed_by?: ReverifyRequestActor;
  failed_by?: ReverifyRequestActor;
}

export interface Timeframe {
  value: number;
  unit: "month" | "year";
}

interface DocumentVerification {
  allowed: boolean;
  timeframe: Timeframe;
}

export enum DocumentVerificationType {
  BankStatement = "bank_statement",
  Paystub = "paystub",
  W2 = "w2",
  BenefitsStatement = "benefits_statement",
  Form1099Nec = "form_1099_nec",
  Form1099Misc = "form_1099_misc"
}

export interface CombinedIncomeConfigs<T> {
  bank_allowed: boolean;
  payroll_allowed: boolean;
  [DocumentVerificationType.BankStatement]: DocumentVerification;
  [DocumentVerificationType.Paystub]: DocumentVerification;
  [DocumentVerificationType.W2]: DocumentVerification;
  [DocumentVerificationType.BenefitsStatement]: DocumentVerification;
  // Self-employment only; missing means off.
  [DocumentVerificationType.Form1099Nec]?: DocumentVerification;
  [DocumentVerificationType.Form1099Misc]?: DocumentVerification;
  fields: T;
}

export type SupervisorDetails = {
  first_name: FieldDisplaySetting;
  last_name: FieldDisplaySetting;
  phone: FieldDisplaySetting;
  email: FieldDisplaySetting;
};

export type CombinedEmployment = CombinedIncomeConfigs<{
  job_title: FieldDisplaySetting;
  supervisor: SupervisorDetails;
}>;

export type CombinedSelfEmployment = CombinedIncomeConfigs<{
  job_title: FieldDisplaySetting;
}>;

export interface CombinedOfferLetter {
  enabled?: boolean;
  fields: {
    job_title: FieldDisplaySetting;
    supervisor: SupervisorDetails;
  };
}

export type CombinedOther = CombinedIncomeConfigs<undefined>;

export type CombinedIncomeVerification =
  | CombinedEmployment
  | CombinedSelfEmployment
  | CombinedOfferLetter
  | CombinedOther;

export interface CombinedIncomeVerificationSchema {
  // combined_income_verification_enabled: boolean;
  housing_assistance_program_enabled: boolean;
  housing_assistance_upload: FieldDisplaySetting;
  employment: CombinedEmployment;
  self_employment: CombinedSelfEmployment;
  offer_letter: CombinedOfferLetter;
  other: CombinedOther;
}

export enum DocumentVerificationProvider {
  Inscribe = "inscribe",
  Ocrolus = "ocrolus"
}

export type BoomFinanceVerificationSettings = {
  boom_finance_verification_enabled?: boolean;
  lender_selection_step_enabled?: boolean;
  /** Show the "I do not need financing" opt-out to applicants. Missing = true (PLT-728). */
  no_financing_option_enabled?: boolean;
  available_lender_ids?: string[];
  loan_details?: {
    purchase_price?: FieldDisplaySetting;
    includes_sales_tax?: FieldDisplaySetting;
    down_payment_amount?: FieldDisplaySetting;
  };
  home_details?: {
    is_known?: FieldDisplaySetting;
    home_model?: FieldDisplaySetting;
    manufacturer?: FieldDisplaySetting;
    home_serial?: FieldDisplaySetting;
    year_built?: FieldDisplaySetting;
    house_length?: FieldDisplaySetting;
    house_width?: FieldDisplaySetting;
    home_condition_type?: FieldDisplaySetting;
    land_type?: FieldDisplaySetting;
    lot_rent?: FieldDisplaySetting;
  };
  income_history?: {
    bonus_income?: FieldDisplaySetting;
    overtime_income?: FieldDisplaySetting;
    commission_income?: FieldDisplaySetting;
    allow_insufficient_history?: boolean;
  };
};
